import React from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Typography,
  Paper,
  Chip,
  useTheme
} from '@mui/material';
import {
  Description as FileIcon,
  Business as ProviderIcon,
  Assignment as SignatureIcon,
  Payment as PaymentIcon,
  CheckCircle as CompletedIcon,
  Cancel as CancelledIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Definición de los pasos del pedido
const steps = [
  {
    id: 'CREATED',
    label: 'Pedido Creado',
    description: 'Se ha subido el archivo de la factura',
    icon: FileIcon,
    color: 'primary'
  },
  {
    id: 'PENDING_SIGNATURE',
    label: 'Pendiente de Firma',
    description: 'Se ha asignado el proveedor y se espera aprobación',
    icon: SignatureIcon,
    color: 'warning'
  },
  {
    id: 'PENDING_TRANSFER',
    label: 'Pendiente de Transferencia',
    description: 'Se ha aprobado y se espera definir el método de pago',
    icon: PaymentIcon,
    color: 'info'
  },
  {
    id: 'PENDING_DIRECT_DEBIT',
    label: 'Pendiente de Giro Domiciliado',
    description: 'Esperando fecha de domiciliación del pago',
    icon: PaymentIcon,
    color: 'warning'
  },
  {
    id: 'PENDING_PAYMENT',
    label: 'Pendiente de Pago',
    description: 'Se ha definido el método de pago y se espera confirmación',
    icon: PaymentIcon,
    color: 'secondary'
  },
  {
    id: 'COMPLETED',
    label: 'Completado',
    description: 'El pedido ha sido pagado y finalizado',
    icon: CompletedIcon,
    color: 'success'
  },
  {
    id: 'CANCELLED',
    label: 'Cancelado',
    description: 'El pedido ha sido cancelado',
    icon: CancelledIcon,
    color: 'error'
  }
];

// Mapeo de estados a índices para el stepper
const statusToStep = {
  'CREATED': 0,
  'PENDING_SIGNATURE': 1,
  'PENDING_TRANSFER': 2,
  'PENDING_DIRECT_DEBIT': 3,
  'PENDING_PAYMENT': 4,
  'COMPLETED': 5,
  'CANCELLED': 6
};

function OrderStepper({ currentStatus, orderData, onStepClick }) {
  const theme = useTheme();
  const currentStep = statusToStep[currentStatus] || 0;

  // Determinar si un paso está activo, completado o pendiente
  const getStepStatus = (stepIndex) => {
    if (currentStatus === 'CANCELLED') {
      return stepIndex === 5 ? 'active' : 'disabled';
    }
    if (stepIndex === currentStep) return 'active';
    if (stepIndex < currentStep) return 'completed';
    return 'disabled';
  };

  // Obtener el color del paso según su estado
  const getStepColor = (stepIndex) => {
    const status = getStepStatus(stepIndex);
    if (status === 'active') return theme.palette[steps[stepIndex].color].main;
    if (status === 'completed') return theme.palette.success.main;
    return theme.palette.text.disabled;
  };

  // Obtener información de completado para un paso
  const getStepCompletionInfo = (stepId) => {
    return orderData?.stepSummary?.[stepId] || null;
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Estado del Pedido
      </Typography>
      
      <Stepper
        activeStep={currentStep}
        orientation="vertical"
        sx={{
          '& .MuiStepLabel-root': {
            cursor: 'pointer'
          },
          '& .MuiStepLabel-iconContainer': {
            paddingRight: 2
          }
        }}
      >
        {steps.map((step, index) => {
          const status = getStepStatus(index);
          const Icon = step.icon;
          const completionInfo = getStepCompletionInfo(step.id);
          
          return (
            <Step
              key={step.id}
              active={status === 'active'}
              completed={status === 'completed'}
              onClick={() => onStepClick && onStepClick(step.id)}
              sx={{
                opacity: status === 'disabled' ? 0.5 : 1,
                cursor: onStepClick ? 'pointer' : 'default',
                '&:hover': {
                  opacity: status === 'disabled' ? 0.5 : 0.8
                }
              }}
            >
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      backgroundColor: getStepColor(index),
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white'
                    }}
                  >
                    <Icon />
                  </Box>
                )}
              >
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: status === 'active' ? 'bold' : 'normal',
                      color: getStepColor(index)
                    }}
                  >
                    {step.label}
                  </Typography>
                  
                  {/* Mostrar información de completado para pasos completados */}
                  {(status === 'completed' || status === 'active') && completionInfo && (
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip
                        icon={<PersonIcon />}
                        label={completionInfo.completedBy}
                        size="small"
                        variant="outlined"
                        color={status === 'completed' ? 'success' : 'primary'}
                      />
                      <Chip
                        icon={<TimeIcon />}
                        label={format(new Date(completionInfo.completedAt), 'dd/MM/yyyy HH:mm', { locale: es })}
                        size="small"
                        variant="outlined"
                        color={status === 'completed' ? 'success' : 'primary'}
                      />
                    </Box>
                  )}
                </Box>
              </StepLabel>
              
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {step.description}
                </Typography>
                
                {/* Información adicional según el estado */}
                {status === 'active' && orderData && (
                  <Box sx={{ mt: 1, pl: 2, borderLeft: `2px solid ${getStepColor(index)}` }}>
                    {step.id === 'CREATED' && orderData.files && orderData.files.length > 0 && (
                      <Typography variant="body2">
                        Archivo: {orderData.files[0].filename}
                      </Typography>
                    )}
                    
                    {step.id === 'PENDING_SIGNATURE' && orderData.provider && (
                      <>
                        <Typography variant="body2">
                          Proveedor: {orderData.provider.name}
                        </Typography>
                        <Typography variant="body2">
                          Empresa: {orderData.company}
                        </Typography>
                        <Typography variant="body2">
                          Total: {orderData.amount ? Number(orderData.amount).toFixed(2) : '0.00'}€
                        </Typography>
                      </>
                    )}
                    
                    {step.id === 'PENDING_TRANSFER' && orderData.paymentMethod && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          Método seleccionado:
                        </Typography>
                        <Typography variant="body2">
                          • {orderData.paymentMethod}
                        </Typography>
                      </>
                    )}
                    
                    {step.id === 'PENDING_PAYMENT' && orderData.paymentDate && (
                      <>
                        <Typography variant="body2">
                          Fecha de pago: {
                            !isNaN(new Date(orderData.paymentDate).getTime())
                              ? format(new Date(orderData.paymentDate), 'dd/MM/yyyy', { locale: es })
                              : 'No definida'
                          }
                        </Typography>
                        {orderData.comments && (
                          <Typography variant="body2">
                            Comentarios: {orderData.comments}
                          </Typography>
                        )}
                      </>
                    )}
                    
                    {step.id === 'COMPLETED' && orderData.paymentDate && (
                      <Typography variant="body2">
                        Fecha de finalización: {
                          !isNaN(new Date(orderData.paymentDate).getTime())
                            ? format(new Date(orderData.paymentDate), 'dd/MM/yyyy', { locale: es })
                            : 'No definida'
                        }
                      </Typography>
                    )}
                    
                    {step.id === 'CANCELLED' && orderData.comments && (
                      <Typography variant="body2" color="error">
                        Motivo: {orderData.comments}
                      </Typography>
                    )}
                  </Box>
                )}
                
                {/* Información de completado para pasos completados */}
                {status === 'completed' && completionInfo && completionInfo.details && (
                  <Box sx={{ mt: 1, p: 1, backgroundColor: theme.palette.success.light + '20', borderRadius: 1 }}>
                    {step.id === 'PENDING_SIGNATURE' && completionInfo.details.providerName && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          Datos asignados:
                        </Typography>
                        <Typography variant="body2">
                          • Proveedor: {completionInfo.details.providerName}
                        </Typography>
                        {completionInfo.details.calculatedTotal && (
                          <Typography variant="body2">
                            • Total: {completionInfo.details.calculatedTotal}€
                          </Typography>
                        )}
                      </>
                    )}
                    
                    {step.id === 'PENDING_TRANSFER' && completionInfo.details && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          Método seleccionado:
                        </Typography>
                        <Typography variant="body2">
                          • {orderData.paymentMethod}
                        </Typography>
                        {/* Mostrar información especial para pago inmediato */}
                        {completionInfo.details.immediatePayment && (
                          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'medium' }}>
                            • Pago inmediato completado
                          </Typography>
                        )}
                      </>
                    )}
                    
                    {step.id === 'COMPLETED' && completionInfo.details.totalAmount && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          Pago completado:
                        </Typography>
                        <Typography variant="body2">
                          • Importe: {completionInfo.details.totalAmount}€
                        </Typography>
                        {completionInfo.details.paymentMethod && (
                          <Typography variant="body2">
                            • Método: {completionInfo.details.paymentMethod}
                          </Typography>
                        )}
                        {completionInfo.paymentDetails && (
                          <>
                            {completionInfo.paymentDetails.paymentDate && (
                              <Typography variant="body2">
                                • Fecha de pago: {format(new Date(completionInfo.paymentDetails.paymentDate), 'dd/MM/yyyy', { locale: es })}
                              </Typography>
                            )}
                            {(completionInfo.paymentDetails.comments || completionInfo.details.comments) && (
                              <Typography variant="body2">
                                • Comentarios: {completionInfo.paymentDetails.comments || completionInfo.details.comments}
                              </Typography>
                            )}
                            {completionInfo.paymentDetails.paymentMethod && (
                              <Typography variant="body2">
                                • Método de pago: {completionInfo.paymentDetails.paymentMethod}
                              </Typography>
                            )}
                          </>
                        )}
                        {/* Fallback para mostrar comentarios si no están en paymentDetails */}
                        {!completionInfo.paymentDetails && completionInfo.details.comments && (
                          <Typography variant="body2">
                            • Comentarios: {completionInfo.details.comments}
                          </Typography>
                        )}
                      </>
                    )}
                    
                    {step.id === 'CANCELLED' && completionInfo.details && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          Pedido cancelado:
                        </Typography>
                        {completionInfo.details.reason && (
                          <Typography variant="body2">
                            • Motivo: {completionInfo.details.reason}
                          </Typography>
                        )}
                        {completionInfo.details.previousStatus && (
                          <Typography variant="body2">
                            • Estado anterior: {completionInfo.details.previousStatus}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </StepContent>
            </Step>
          );
        })}
      </Stepper>
    </Paper>
  );
}

export default OrderStepper; 